// src/components/NotificationBell.jsx
// Shows unread notification count + dropdown panel for followers.

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listenUnreadCount,
  listenNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../firebase-utils';

export default function NotificationBell({ onJoinLive }) {
  const { currentUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [ringing, setRinging] = useState(false);
  const prevCountRef = useRef(0);
  const panelRef = useRef(null);

  // Listen to unread count
  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenUnreadCount(currentUser.uid, (count) => {
      setUnreadCount(count);
      // Trigger ring animation when a new notification arrives
      if (count > prevCountRef.current && prevCountRef.current !== null) {
        setRinging(true);
        setTimeout(() => setRinging(false), 1000);
      }
      prevCountRef.current = count;
    });
    return () => unsub();
  }, [currentUser]);

  // Listen to notifications when panel opens
  useEffect(() => {
    if (!currentUser || !open) return;
    const unsub = listenNotifications(currentUser.uid, setNotifications);
    return () => unsub();
  }, [currentUser, open]);

  // Mark all read when panel opens
  useEffect(() => {
    if (!open || !currentUser || unreadCount === 0) return;
    markAllNotificationsRead(currentUser.uid).catch(console.warn);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!currentUser) return null;

  const timeAgo = (ts) => {
    if (!ts) return '';
    const ms = ts?.seconds ? ts.seconds * 1000 : new Date(ts).getTime();
    const diff = Date.now() - ms;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div ref={panelRef} style={{ position: 'relative', display: 'inline-block' }}>

      {/* Bell animation keyframes */}
      <style>{`
        @keyframes bellRing {
          0%   { transform: rotate(0deg); }
          10%  { transform: rotate(18deg); }
          20%  { transform: rotate(-16deg); }
          30%  { transform: rotate(14deg); }
          40%  { transform: rotate(-12deg); }
          50%  { transform: rotate(10deg); }
          60%  { transform: rotate(-8deg); }
          70%  { transform: rotate(6deg); }
          80%  { transform: rotate(-4deg); }
          90%  { transform: rotate(2deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes badgePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
          50%       { transform: scale(1.2); box-shadow: 0 0 0 6px rgba(239,68,68,0); }
        }
        @keyframes bellGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(168,85,247,0); }
          50%       { box-shadow: 0 0 0 8px rgba(168,85,247,0.25); }
        }
      `}</style>

      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative',
          background: open ? 'rgba(168,85,247,0.2)' : unreadCount > 0 ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.08)',
          border: open ? '1px solid rgba(168,85,247,0.5)' : unreadCount > 0 ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.2)',
          borderRadius: '50%',
          width: '44px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '20px',
          transition: 'all 0.2s',
          animation: ringing ? 'bellRing 1s ease, bellGlow 1s ease' : unreadCount > 0 ? 'bellGlow 2s ease infinite' : 'none',
          transformOrigin: 'top center',
        }}>
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: '#ef4444',
            color: 'white',
            fontSize: '11px',
            fontWeight: 'bold',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #0a0a0a',
            animation: ringing ? 'badgePulse 0.5s ease 2' : 'badgePulse 2s ease infinite',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification panel */}
      {open && (
        <div style={{
          position: 'absolute',
          top: '52px',
          right: 0,
          width: '340px',
          maxHeight: '420px',
          overflowY: 'auto',
          background: 'rgba(10,10,20,0.98)',
          border: '1px solid rgba(168,85,247,0.3)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          zIndex: 1000,
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <p style={{ color: 'white', fontWeight: 'bold', fontSize: '15px' }}>
              🔔 Notifications
            </p>
            {notifications.length > 0 && (
              <button
                onClick={() => markAllNotificationsRead(currentUser.uid)}
                style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}>
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          {notifications.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center' }}>
              <p style={{ fontSize: '28px', marginBottom: '8px' }}>🎵</p>
              <p style={{ color: '#6b7280', fontSize: '14px' }}>No notifications yet.</p>
              <p style={{ color: '#4b5563', fontSize: '12px', marginTop: '4px' }}>
                Follow artists to get alerts when they go live or schedule gigs.
              </p>
            </div>
          ) : (
            notifications.map(notif => (
              <div
                key={notif.id}
                onClick={() => markNotificationRead(currentUser.uid, notif.id)}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  background: notif.read ? 'transparent' : 'rgba(168,85,247,0.06)',
                  cursor: 'default',
                  transition: 'background 0.15s',
                }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  {/* Icon */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    background: notif.type === 'artist_live'
                      ? 'rgba(239,68,68,0.15)'
                      : 'rgba(0,212,255,0.12)',
                  }}>
                    {notif.type === 'artist_live' ? '🔴' : '🎸'}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      color: notif.read ? '#d1d5db' : 'white',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      marginBottom: '4px',
                    }}>
                      {notif.message}
                    </p>
                    <p style={{ color: '#6b7280', fontSize: '11px' }}>
                      {timeAgo(notif.createdAt)}
                    </p>
                  </div>

                  {/* Unread dot */}
                  {!notif.read && (
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#a855f7',
                      flexShrink: 0,
                      marginTop: '4px',
                    }} />
                  )}
                </div>

                {/* Join live button */}
                {notif.type === 'artist_live' && notif.gigId && onJoinLive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onJoinLive(notif.gigId, notif.artistId);
                      setOpen(false);
                    }}
                    style={{
                      marginTop: '8px',
                      marginLeft: '46px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      border: '1px solid rgba(239,68,68,0.4)',
                      background: 'rgba(239,68,68,0.12)',
                      color: '#f87171',
                      cursor: 'pointer',
                    }}>
                    🎵 Join Live Now
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
